import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { portalApi, type PortalProfile } from '@/api/portal';

function validateCallbackUrl(value: string, label: string) {
  const raw = value.trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    if (!['http:', 'https:'].includes(parsed.protocol)) return `${label}仅允许 http 或 https`;
    if (parsed.username || parsed.password) return `${label}不能包含用户名或密码`;
    const host = parsed.hostname.toLowerCase();
    if (!host || host === 'localhost' || host.endsWith('.localhost') || host === '127.0.0.1' || host === '0.0.0.0' || host === '::1') {
      return `${label}不能使用本机地址`;
    }
    return '';
  } catch {
    return `${label}格式不正确`;
  }
}

export default function SettingsPage() {
  const [profile, setProfile] = useState<PortalProfile | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    portalApi.profile().then(setProfile).catch((err) => setError(err instanceof Error ? err.message : '加载失败'));
  }, []);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!profile) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const urlError = validateCallbackUrl(profile.notifyUrl || '', '异步通知地址') || validateCallbackUrl(profile.returnUrl || '', '同步跳转地址');
      if (urlError) {
        setError(urlError);
        return;
      }
      const newPassword = password.trim();
      if (newPassword && (newPassword.length < 6 || newPassword.length > 64)) {
        setError('新密码长度必须为6到64个字符');
        return;
      }
      if (newPassword !== confirmPassword.trim()) {
        setError('两次输入的新密码不一致');
        return;
      }
      const payload = {
        contactName: profile.contactName,
        contactPhone: profile.contactPhone,
        notifyUrl: profile.notifyUrl,
        returnUrl: profile.returnUrl,
        ...(newPassword ? { password: newPassword } : {}),
      };
      await portalApi.updateProfile(payload);
      setPassword('');
      setConfirmPassword('');
      setMessage(newPassword ? '商户配置和登录密码已保存' : '商户配置已保存');
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6">
        {message && <div className="rounded-lg bg-green-500/10 border border-green-500/20 px-4 py-3 text-sm text-green-300">{message}</div>}
        {error && <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">{error}</div>}
        {!profile ? (
          <div className="tech-card p-10 text-center text-slate-400">正在加载...</div>
        ) : (
          <form onSubmit={save} className="tech-card p-5 md:p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">商户号</label>
              <input value={profile.merchantNo} readOnly className="w-full rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm text-slate-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">商户名称</label>
              <input value={profile.name} readOnly className="w-full rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm text-slate-500" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">联系人</label>
                <input value={profile.contactName || ''} onChange={(e) => setProfile({ ...profile, contactName: e.target.value })} className="tech-input w-full px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">联系电话</label>
                <input value={profile.contactPhone || ''} onChange={(e) => setProfile({ ...profile, contactPhone: e.target.value })} className="tech-input w-full px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">默认异步通知地址</label>
              <input value={profile.notifyUrl || ''} onChange={(e) => setProfile({ ...profile, notifyUrl: e.target.value })} placeholder="https://example.com/pay/notify" className="tech-input w-full px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">默认同步跳转地址</label>
              <input value={profile.returnUrl || ''} onChange={(e) => setProfile({ ...profile, returnUrl: e.target.value })} placeholder="https://example.com/pay/return" className="tech-input w-full px-3 py-2 text-sm" />
            </div>
            <div className="border-t border-slate-700/70 pt-4">
              <div className="mb-3">
                <h3 className="text-sm font-medium text-slate-200">修改登录密码</h3>
                <p className="mt-1 text-xs text-slate-500">无需修改时请留空，保存配置不会影响当前密码。</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">新密码</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} maxLength={64} autoComplete="new-password" placeholder="留空则不修改" className="tech-input w-full px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">确认新密码</label>
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} minLength={6} maxLength={64} autoComplete="new-password" placeholder="再次输入新密码" className="tech-input w-full px-3 py-2 text-sm" />
                </div>
              </div>
            </div>
            <div className="pt-1">
              <button type="submit" disabled={saving} className="tech-btn-primary inline-flex items-center gap-2 px-5 py-2 text-sm disabled:opacity-60">
                {saving ? '保存中...' : '保存配置'}
              </button>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}
